import { Alert, Button } from "@mui/material";
import { useMemo, useState } from "react";

import { useTranslation } from "@/context/I18nProvider";
import { logoutUser } from "@/services";
import { dispatch, logout, useSelector } from "@/store";

// The session token already carries who opened it; it is read here only to show the warning.
const impersonatorOf = (token?: string | null): string | null => {
  try {
    const part = (token ?? "").split(".")[1];
    if (!part) return null;
    const bytes = Uint8Array.from(atob(part.replace(/-/g, "+").replace(/_/g, "/")), (char) =>
      char.charCodeAt(0),
    );
    const payload = JSON.parse(new TextDecoder().decode(bytes));
    return payload.impBy ? String(payload.impByName || payload.impBy) : null;
  } catch {
    return null;
  }
};

/** Always on screen during a login-as, so nobody forgets whose account they are in. */
const ImpersonationBanner = () => {
  const { t } = useTranslation();
  const { token, user } = useSelector((state) => state.auth);
  const [ending, setEnding] = useState<boolean>(false);
  const impersonator = useMemo(() => impersonatorOf(token), [token]);

  if (!impersonator) return null;

  const handleEnd = async () => {
    setEnding(true);
    try {
      await logoutUser();
    } catch {
      // The session ends here either way; leaving the page signed in would be worse.
    }
    dispatch(logout());
  };

  return (
    <Alert
      severity="warning"
      sx={{ width: "100%" }}
      action={
        <Button color="inherit" size="small" disabled={ending} onClick={handleEnd}>
          {t("users.endLoginAs")}
        </Button>
      }
    >
      {t("users.loginAsBanner", { username: user?.username ?? "", admin: impersonator })}
    </Alert>
  );
};

export default ImpersonationBanner;
